import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Clock, Users, Fish, Waves, Ship, Sunset, MapPin, Anchor, Heart, Sparkles, Leaf, Loader2, Sun, Umbrella, Tent, Flame, Camera, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { client } from '../api/client';
import { localizedName, localizedDescription } from '../lib/localized';

interface Experience {
  id: string;
  name: string;
  nameEn: string | null;
  nameZh: string | null;
  nameZhCn: string | null;
  duration: string;
  groupSize: string;
  includes: string[];
  description: string;
  descriptionEn: string | null;
  descriptionZh: string | null;
  descriptionZhCn: string | null;
  price?: number | null;
  priceNote: string;
  image: string;
  icon: React.ReactNode;
  category: string;
}

interface ApiExperience {
  id: number;
  name: string;
  nameEn: string | null;
  nameZh: string | null;
  nameZhCn: string | null;
  slug: string;
  description: string;
  descriptionEn: string | null;
  descriptionZh: string | null;
  descriptionZhCn: string | null;
  duration: string;
  groupSize: string;
  includes: string;
  price: number | null;
  priceNote: string;
  imageUrl: string;
  iconName: string | null;
  sortOrder: number;
  status: string;
}

interface ApiRetreat {
  id: number;
  name: string;
  nameEn: string | null;
  nameZh: string | null;
  nameZhCn: string | null;
  slug: string;
  description: string;
  descriptionEn: string | null;
  descriptionZh: string | null;
  descriptionZhCn: string | null;
  duration: string;
  location: string;
  audience: string;
  itinerary: string;
  priceNote: string;
  imageUrl: string;
  iconName: string | null;
  sortOrder: number;
  status: string;
}

function safeJsonParse<T>(value: string | null, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Fish,
  Waves,
  Sunset,
  Ship,
  Sparkles,
  MapPin,
  Anchor,
  Heart,
  Leaf,
  Sun,
  Umbrella,
  Tent,
  Flame,
  Camera,
};

function getIconByName(name: string | null): React.ReactNode {
  if (!name) return <Sparkles className="w-5 h-5" />;
  const Icon = iconMap[name];
  if (!Icon) return <Sparkles className="w-5 h-5" />;
  return <Icon className="w-5 h-5" />;
}

const categoryMap: Record<string, string> = {
  'coral-garden-snorkeling': 'snorkeling',
  'two-spot-snorkeling': 'snorkeling',
  'shipwreck-manta-snorkeling': 'snorkeling',
  'turtle-snorkeling': 'snorkeling',
  'scuba-diving-experience': 'snorkeling',
  'whale-shark-manta': 'snorkeling',
  'traditional-night-fishing': 'fishing',
  'deep-sea-big-game-fishing': 'fishing',
  'half-day-sea-fishing': 'fishing',
  'full-day-sea-fishing': 'fishing',
  'sea-fishing': 'fishing',
  'guided-surf-trip': 'surfing',
  'surf-lesson': 'surfing',
  'group-yoga-class': 'surfing',
  'private-yoga-session': 'surfing',
  'local-island-culture-tour': 'exploration',
  'uninhabited-island-half-day': 'exploration',
  'uninhabited-island-full-day': 'exploration',
  'uninhabited-island': 'exploration',
  'sunset-cruise': 'exploration',
  'private-island-picnic-half-day': 'romance',
  'private-island-picnic-full-day': 'romance',
  'private-island-picnic': 'romance',
  'private-island-overnight-camping': 'romance',
  'private-island-bbq-dinner': 'romance',
  'couples-romantic-beach-dinner': 'romance',
  'drone-aerial-photography': 'aerial',
};

const categoryOrder = [
  'snorkeling',
  'fishing',
  'surfing',
  'exploration',
  'romance',
  'aerial',
];

const categoryMeta: Record<string, { image: string; iconName: string }> = {
  all: { image: 'https://images.unsplash.com/photo-1544550581-5f7ceaf7f992?w=800&q=80', iconName: 'Sparkles' },
  snorkeling: { image: 'https://images.unsplash.com/photo-1540202404-a2f29016b523?w=800&q=80', iconName: 'Waves' },
  fishing: { image: 'https://images.unsplash.com/photo-1500514966906-fe245eea9344?w=800&q=80', iconName: 'Fish' },
  surfing: { image: 'https://images.unsplash.com/photo-1502680390469-be75c86b636f?w=800&q=80', iconName: 'Sun' },
  exploration: { image: 'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=800&q=80', iconName: 'Ship' },
  romance: { image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80', iconName: 'Heart' },
  aerial: { image: 'https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?w=800&q=80', iconName: 'Camera' },
  retreats: { image: 'https://images.unsplash.com/photo-1545205597-3d035aa65de?w=800&q=80', iconName: 'Leaf' },
};

function mapApiExperience(item: ApiExperience): Experience {
  return {
    id: item.slug,
    name: item.name,
    nameEn: item.nameEn,
    nameZh: item.nameZh,
    nameZhCn: item.nameZhCn,
    duration: item.duration,
    groupSize: item.groupSize,
    includes: safeJsonParse<string[]>(item.includes, []),
    description: item.description,
    descriptionEn: item.descriptionEn,
    descriptionZh: item.descriptionZh,
    descriptionZhCn: item.descriptionZhCn,
    price: item.price,
    priceNote: item.priceNote,
    image: item.imageUrl,
    icon: getIconByName(item.iconName),
    category: categoryMap[item.slug] || 'other',
  };
}

function mapApiRetreat(item: ApiRetreat): Experience {
  const itinerary = safeJsonParse<{ day: string; title: string; desc: string }[]>(item.itinerary, []);
  return {
    id: item.slug,
    name: item.name,
    nameEn: item.nameEn,
    nameZh: item.nameZh,
    nameZhCn: item.nameZhCn,
    duration: item.duration,
    groupSize: item.audience,
    includes: itinerary.length > 0 ? itinerary.slice(0, 4).map((it) => it.title) : [],
    description: item.description,
    descriptionEn: item.descriptionEn,
    descriptionZh: item.descriptionZh,
    descriptionZhCn: item.descriptionZhCn,
    priceNote: item.priceNote,
    image: item.imageUrl,
    icon: getIconByName(item.iconName),
    category: 'retreats',
  };
}

export default function ExperiencesPage() {
  const { t } = useTranslation('experiences');
  const [apiExperiences, setApiExperiences] = useState<Experience[]>([]);
  const [apiRetreats, setApiRetreats] = useState<Experience[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>(categoryOrder[0]);
  const [selectedExperience, setSelectedExperience] = useState<Experience | null>(null);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const [expRes, retRes] = await Promise.all([
          client.api.fetch('/api/public/experiences'),
          client.api.fetch('/api/public/retreats'),
        ]);
        if (!expRes.ok) {
          throw new Error(`Experiences API error ${expRes.status}`);
        }
        const expJson = (await expRes.json()) as { data?: ApiExperience[] };
        const expList = Array.isArray(expJson?.data) ? expJson.data.map(mapApiExperience) : [];

        let retList: Experience[] = [];
        if (retRes.ok) {
          const retJson = (await retRes.json()) as { data?: ApiRetreat[] };
          retList = Array.isArray(retJson?.data) ? retJson.data.map(mapApiRetreat) : [];
        } else {
          console.warn('Retreats API error:', retRes.status);
        }

        if (mounted) {
          setApiExperiences(expList);
          setApiRetreats(retList);
        }
      } catch (err) {
        console.error('Experiences load error:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Unknown error');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const combinedItems = [...apiExperiences, ...apiRetreats];

  const filteredItems = combinedItems.filter((item) => item.category === activeCategory);

  const categoryCounts = combinedItems.reduce<Record<string, number>>((acc, item) => {
    acc[item.category] = (acc[item.category] || 0) + 1;
    return acc;
  }, {});

  const categoryCards = [
    ...categoryOrder.map((c) => ({ key: c, label: t(`categories.${c}`) })),
    ...(apiRetreats.length > 0 ? [{ key: 'retreats', label: t('categories.retreats') }] : []),
  ];

  const retreatFallbackIncludes = [
    t('retreat.fallback.accommodation'),
    t('retreat.fallback.dailyActivities'),
    t('retreat.fallback.meals'),
    t('retreat.fallback.butler'),
  ];

  return (
    <div className="pt-20 pb-16">
      {/* Hero — Desktop */}
      <div className="relative hidden md:flex md:min-h-[600px] md:h-[75vh] items-end justify-center overflow-hidden pb-16">
        <img
          src="https://uploadfile.jkdcoding.com/experience.jpeg"
          alt={t('hero.alt')}
          className="absolute inset-0 w-full h-full object-cover object-[35%_center]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a4c6b]/95 via-[#0a4c6b]/40 to-[#0a4c6b]/10" />
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <p className="text-[#B8902F] font-medium mb-2 tracking-widest uppercase text-sm">{t('hero.eyebrow')}</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">{t('hero.title')}</h1>
          <p className="text-xl md:text-2xl text-white/90 mb-3">{t('hero.subtitle')}</p>
          <p className="text-white/70 max-w-2xl mx-auto">{t('hero.description')}</p>
        </div>
      </div>

      {/* Hero — Mobile */}
      <section className="md:hidden min-h-screen flex flex-col bg-[#0d1b2a]">
        <div className="relative h-[55vh] w-full">
          <img
            src="https://uploadfile.jkdcoding.com/experience.jpeg"
            alt={t('hero.alt')}
            className="w-full h-full object-cover object-[50%_35%]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a4c6b]/40 via-transparent to-[#0d1b2a]" />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 py-10 text-center text-white">
          <div className="max-w-xl">
            <p className="text-[#B8902F] font-medium mb-2 tracking-widest uppercase text-sm">{t('hero.eyebrow')}</p>
            <h1 className="text-4xl font-bold mb-4">{t('hero.title')}</h1>
            <p className="text-lg text-white/90 mb-3">{t('hero.subtitle')}</p>
            <p className="text-white/70">{t('hero.description')}</p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[#2ec4b6] font-medium mb-2">{t('intro.eyebrow')}</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2a] mb-6">{t('intro.title')}</h2>
            <p className="text-gray-600 leading-relaxed">
              {t('intro.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Activities Grid */}
      <section className="py-16 px-4 bg-[#f8fafb]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {categoryCards.map((cat, i) => {
              const meta = categoryMeta[cat.key] || categoryMeta.all;
              const count = cat.key === 'all' ? combinedItems.length : (categoryCounts[cat.key] || 0);
              const Icon = iconMap[meta.iconName] || Sparkles;
              return (
                <motion.button
                  key={cat.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`relative overflow-hidden rounded-2xl text-left h-40 transition focus:outline-none ${
                    activeCategory === cat.key
                      ? 'ring-4 ring-[#B8902F] shadow-xl'
                      : 'hover:shadow-lg'
                  }`}
                >
                  <img
                    src={meta.image}
                    alt={cat.label}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a4c6b]/90 via-[#0a4c6b]/40 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-5 h-5 text-[#B8902F]" />
                      <span className="text-sm font-medium text-white/80">{t('categoryCount', { count })}</span>
                    </div>
                    <h3 className="text-xl font-bold">{cat.label}</h3>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-[#0a4c6b] animate-spin mb-4" />
              <p className="text-gray-500">{t('loading')}</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-600 mb-2">{t('error.message')}</p>
              <p className="text-sm text-gray-500">{error}</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-600">{t('empty')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredItems.map((exp, i) => {
                const displayIncludes = exp.category === 'retreats' && exp.includes.length === 0
                  ? retreatFallbackIncludes
                  : exp.includes;
                return (
                  <motion.div
                    key={exp.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition duration-300"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden cursor-pointer group">
                      <img
                        src={exp.image}
                        alt={localizedName(exp)}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-[#0d1b2a]/0 group-hover:bg-[#0d1b2a]/60 transition-all duration-300 flex items-center justify-center">
                        <span className="text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm border border-white px-4 py-2 rounded">
                          {t('cta.learnMore')} →
                        </span>
                      </div>
                      <div className="absolute top-4 left-4 bg-[#B8902F] text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5">
                        {exp.icon}
                        <span>{localizedName(exp)}</span>
                      </div>

                    </div>
                    <div className="p-6">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="text-xl font-bold text-[#0d1b2a]">{localizedName(exp)}</h3>
                        <span className="shrink-0 inline-block bg-[#0a4c6b]/10 text-[#0a4c6b] text-xs font-medium px-2.5 py-1 rounded-full">
                          {t(`categories.${exp.category}`)}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mt-3 leading-relaxed">{localizedDescription(exp)}</p>

                      <div className="mt-4 space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4 text-[#2ec4b6]" />
                          <span>{exp.duration}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Users className="w-4 h-4 text-[#2ec4b6]" />
                          <span>{t('groupSize', { size: exp.groupSize })}</span>
                        </div>
                      </div>

                      <div className="mt-4">
                        <p className="text-xs text-gray-500 mb-2">{t('includesLabel')}</p>
                        <div className="flex flex-wrap gap-2">
                          {displayIncludes.slice(0, 4).map((item, idx) => (
                            <span
                              key={idx}
                              className="text-xs bg-[#f0f9f7] text-[#0a4c6b] px-2 py-1 rounded-full flex items-center gap-1"
                            >
                              <Check className="w-3 h-3" />
                              {item}
                            </span>
                          ))}
                          {displayIncludes.length > 4 && (
                            <span className="text-xs text-gray-400 px-2 py-1">+{displayIncludes.length - 4}</span>
                          )}
                        </div>
                      </div>

                      <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between gap-4">
                        {exp.price != null ? (
                          <p className="text-lg font-bold text-[#0a4c6b]">HK${exp.price.toLocaleString()}</p>
                        ) : (
                          <p className="text-sm text-gray-500">{exp.priceNote}</p>
                        )}
                        <button
                          onClick={() => setSelectedExperience(exp)}
                          className="bg-[#0a4c6b] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#083d56] transition"
                        >
                          {t('cta.learnMore')}
                        </button>
                      </div>

                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Experience Detail Modal */}
      <AnimatePresence>
        {selectedExperience && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setSelectedExperience(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="relative aspect-video">
                <img
                  src={selectedExperience.image}
                  alt={localizedName(selectedExperience)}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <button
                  onClick={() => setSelectedExperience(null)}
                  className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-700 rounded-full p-2 transition"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="absolute bottom-4 left-4 text-white">
                  <span className="inline-block bg-[#B8902F] text-xs font-medium px-3 py-1 rounded-full mb-2">
                    {t(`categories.${selectedExperience.category}`)}
                  </span>
                  <h3 className="text-2xl font-bold">{localizedName(selectedExperience)}</h3>
                </div>
              </div>
              <div className="p-6 sm:p-8 space-y-6">
                <p className="text-gray-600 leading-relaxed">{localizedDescription(selectedExperience)}</p>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 bg-[#f8fafb] rounded-xl p-4">
                    <Clock className="w-5 h-5 text-[#2ec4b6]" />
                    <div>
                      <p className="text-xs text-gray-500">{t('modal.duration')}</p>
                      <p className="font-medium text-[#0d1b2a]">{selectedExperience.duration}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-[#f8fafb] rounded-xl p-4">
                    <Users className="w-5 h-5 text-[#2ec4b6]" />
                    <div>
                      <p className="text-xs text-gray-500">{t('modal.groupSize')}</p>
                      <p className="font-medium text-[#0d1b2a]">{selectedExperience.groupSize}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-[#0d1b2a] mb-3">{t('modal.includes')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {(selectedExperience.category === 'retreats' && selectedExperience.includes.length === 0
                      ? retreatFallbackIncludes
                      : selectedExperience.includes
                    ).map((item, idx) => (
                      <span
                        key={idx}
                        className="text-sm bg-[#f0f9f7] text-[#0a4c6b] px-3 py-1.5 rounded-full flex items-center gap-1"
                      >
                        <Check className="w-3.5 h-3.5" />
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  {selectedExperience.price != null ? (
                    <p className="text-lg font-bold text-[#0a4c6b]">HK${selectedExperience.price.toLocaleString()}</p>
                  ) : (
                    <p className="text-sm text-gray-500">{selectedExperience.priceNote}</p>
                  )}
                  <a
                    href="/plan"
                    className="bg-[#B8902F] text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#9a7a28] transition"
                  >
                    {t('cta.bookConsultation')}
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Trust / CTA */}
      <section className="py-16 px-4 bg-[#0a4c6b]">
        <div className="max-w-5xl mx-auto text-center text-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              { icon: <Anchor className="w-8 h-8" />, titleKey: 'trust.localTeam.title', descKey: 'trust.localTeam.desc' },
              { icon: <Heart className="w-8 h-8" />, titleKey: 'trust.tailored.title', descKey: 'trust.tailored.desc' },
              { icon: <Sparkles className="w-8 h-8" />, titleKey: 'trust.concierge.title', descKey: 'trust.concierge.desc' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="text-[#B8902F] mb-3 flex justify-center">{item.icon}</div>
                <h3 className="font-bold text-lg mb-2">{t(item.titleKey)}</h3>
                <p className="text-white/70 text-sm">{t(item.descKey)}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-white/60 text-sm max-w-2xl mx-auto">
            {t('trust.disclaimer')}
          </p>
        </div>
      </section>
    </div>
  );
}
